import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table'
import { Select, Table, Tbody, Td, Tfoot, Th, Thead, Tr } from '@chakra-ui/react'
import { TooltipCellRenderer2 } from '../components/Cell/ToolTipCell/TooltipCell'
import { RawTextCell } from '../components/Cell'

const defaultColumn = {
  cell: RawTextCell,
  size: 200,
  minSize: 50,
}

const EditableTable = ({
  columnDef,
  data,
  updateData = () => {},
  disabled = false,
  showFooter = false,
  tableWidth = '',
  handleRowClick = () => {},
  canSelect = false,
  rowSelection = [],
  setRowSelection = () => {},
  hiddenColumns = [],
  textAlign = 'center',
  ...rest
}) => {
  const tableInstance = useReactTable({
    data,
    columns: columnDef,
    defaultColumn,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: canSelect,
    ...rest,
    meta: {
      updateData: updateData,
      disabled: disabled,
    },
  })

  if (canSelect) {
    tableInstance.setOptions((prev) => ({
      ...prev,
      state: {
        ...prev.state,
        rowSelection: rowSelection,
      },
      onRowSelectionChange: setRowSelection,
    }))
  }

  return (
    <Table
      variant="simple"
      border="1px solid rgb(226, 232, 240)"
      borderRadius="md"
      layout={'fixed'}
      w={tableWidth === '' ? tableInstance.getCenterTotalSize() : tableWidth}
    >
      <Thead>
        {tableInstance.getHeaderGroups().map((headerGroup) => (
          <Tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <Th
                key={header.id}
                p={2}
                textAlign={textAlign}
                w={header.getSize() !== 0 ? header.getSize() : undefined}
                colSpan={header.colSpan}
                bg={header.column.columnDef?.backgroundColor}
              >
                {flexRender(header.column.columnDef.header, header.getContext())}
              </Th>
            ))}
          </Tr>
        ))}
      </Thead>
      <Tbody>
        {tableInstance.getRowModel().rows.map((row, rowIndex) => {
          return (
            <Tr key={rowIndex} onClick={() => handleRowClick(row)}>
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  p={2}
                  textAlign={textAlign}
                  bg={cell.column.columnDef?.backgroundColor}
                  w={cell.column.getSize() !== 0 ? cell.column.getSize() : undefined}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          )
        })}
      </Tbody>

      {showFooter && (
        <Tfoot>
          {tableInstance.getFooterGroups().map((footerGroup) => (
            <Tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <Td
                  key={header.id}
                  colSpan={header.colSpan}
                  textAlign={textAlign}
                  bg={header.column.columnDef?.backgroundColor}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.footer, header.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tfoot>
      )}
    </Table>
  )
}

export default EditableTable
